import React from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, KeyRound, Store } from "lucide-react";

// Panel «Su cuenta de prueba» de la ficha del lead (13/10/2026, David: «quiero llamarle para
// ver cómo va y que aquí salgan sus accesos»). Sale con la respuesta de /ventas/accesos-status,
// tanto si la cuenta la creamos desde ventas como si el cliente se dio de alta solo en la web
// (entonces se encuentra por su móvil: `resolved_by: "phone"`). «Ver cómo va su prueba» abre
// Supervisión de pruebas con su empresa y sus estadísticas.

const fecha = (iso, lang) => {
  if (!iso) return null;
  // `trial_expires_at` vive como ISO en unas empresas y en segundos epoch en otras.
  const n = Number(iso);
  const d = Number.isFinite(n) ? new Date(n < 1e12 ? n * 1000 : n) : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang || "es-ES", { day: "numeric", month: "short", year: "numeric" });
};

export const supervisionUrl = (companyId) =>
  `/supervision-tablets?empresa=${encodeURIComponent(companyId)}`;

export default function CuentaPruebaPanel({ status, onVerAccesos, canViewCredentials }) {
  const { t, i18n } = useTranslation();
  if (!status?.company_id || !status.exists) return null;
  const lang = i18n.language;
  const altaSola = status.resolved_by === "phone" || status.signup_origin === "appointments_landing";
  const estado =
    status.company_status === "trial"
      ? t("cuentaPrueba.enPrueba", "En prueba")
      : status.company_status === "active"
        ? t("cuentaPrueba.dePago", "De pago")
        : status.company_status || "—";
  const filas = [
    [t("cuentaPrueba.empresa", "Empresa"), status.company_nombre || status.company_id],
    [t("cuentaPrueba.estado", "Estado"), estado],
    status.company_created_at && [
      t("cuentaPrueba.alta", "Alta"),
      `${fecha(status.company_created_at, lang)}${altaSola ? ` · ${t("cuentaPrueba.altaSola", "se dio de alta sola en la web")}` : ""}`,
    ],
    status.trial_expires_at && [t("cuentaPrueba.pruebaHasta", "Prueba hasta"), fecha(status.trial_expires_at, lang)],
    [t("cuentaPrueba.usuario", "Usuario"), status.username || "—"],
    [
      t("cuentaPrueba.contrasena", "Contraseña"),
      status.temp_password
        ? t("cuentaPrueba.contrasenaVer", "pulsa «Ver accesos»")
        : t("cuentaPrueba.contrasenaSuya", "la eligió el cliente al darse de alta"),
    ],
    status.last_login_at && [t("cuentaPrueba.ultimoAcceso", "Último acceso"), fecha(status.last_login_at, lang)],
  ].filter(Boolean);

  return (
    <div className="space-y-4 mt-10 max-w-2xl" data-testid="cuenta-prueba-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="p-2.5 bg-violet-50 text-violet-600 rounded-2xl">
          <Store size={18} />
        </div>
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.15em]">
          {t("cuentaPrueba.titulo", "Su cuenta de prueba")}
        </h3>
        <a
          href={supervisionUrl(status.company_id)}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors"
        >
          <BarChart3 size={13} /> {t("cuentaPrueba.verComoVa", "Ver cómo va su prueba")}
        </a>
      </div>
      <div className="bg-white p-4 rounded-2xl border border-violet-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {filas.map(([k, v]) => (
          <div key={k} className="flex items-baseline gap-2 min-w-0">
            <span className="text-[11px] font-bold text-slate-500 uppercase shrink-0">{k}</span>
            <span className="text-sm text-slate-800 truncate" title={String(v)}>{v}</span>
          </div>
        ))}
        {canViewCredentials && onVerAccesos && (
          <button
            type="button"
            onClick={onVerAccesos}
            className="sm:col-span-2 justify-self-start mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold hover:bg-amber-100"
          >
            <KeyRound size={13} /> {t("cuentaPrueba.verAccesos", "Ver accesos")}
          </button>
        )}
      </div>
    </div>
  );
}
